/**
 * Today's check-in and completion state
 * Simplified: DailyActionPlan is handled by useDailyActionPlanStore
 * Performance optimized with shallow selectors
 */

import { create } from 'zustand';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { submitCheckin, recordCompletion, getErrorMessage } from '../lib/api';
import { getTodayDate } from '../lib/dates';
import { normalizeCheckin } from '../lib/firestoreHelpers';
import type {
  CheckinDocument,
  CreditsSummary,
  RecordCompletionResponse,
} from '../lib/types';

interface TodayState {
  checkin: CheckinDocument | null;
  completedToday: string[];
  creditsSummary: CreditsSummary;
  /** LLM-generated personalized affirmation for the day */
  dailyAffirmation: string | null;
  loading: boolean;
  error: string | null;
  lastReward: RecordCompletionResponse | null;

  // Actions
  loadToday: (uid: string, timezone?: string) => Promise<void>;
  doCheckin: (data: {
    mood: number;
    energy?: number;
    stress?: number;
    noteText?: string;
    noteSaved?: boolean;
  }) => Promise<void>;
  completeAction: (actionId: string, helped?: boolean | null) => Promise<RecordCompletionResponse>;
  clearError: () => void;
  clearReward: () => void;
}

export const useTodayStore = create<TodayState>((set) => ({
  checkin: null,
  completedToday: [],
  creditsSummary: { momentum7d: 0, totalCredits: 0 },
  dailyAffirmation: null,
  loading: false,
  error: null,
  lastReward: null,

  loadToday: async (uid, timezone?: string) => {
    // If we already have a checkin or are currently loading, don't reload
    // (prevents re-fetching after doCheckin and duplicate concurrent requests)
    const currentState = useTodayStore.getState();
    if (currentState.checkin || currentState.loading) {
      return;
    }

    set({ loading: true, error: null });

    try {
      // Get user timezone from userDoc or use provided/detected
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      const userTimezone = timezone || userData?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const today = getTodayDate(userTimezone);

      // Fetch today's check-in
      const checkinsRef = collection(db, 'users', uid, 'checkins');
      const checkinQuery = query(
        checkinsRef,
        where('date', '==', today),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      const checkinSnap = await getDocs(checkinQuery);
      const checkin = checkinSnap.empty
        ? null
        : normalizeCheckin(checkinSnap.docs[0].id, checkinSnap.docs[0].data());

      // Fetch today's completions (for legacy actionLibrary-based actions)
      const completionsRef = collection(db, 'users', uid, 'completions');
      const completionsQuery = query(completionsRef, where('date', '==', today));
      const completionsSnap = await getDocs(completionsQuery);
      const completedToday = completionsSnap.docs.map(d => d.data().actionId);

      // Only use affirmation if it was generated today
      const dailyAffirmation = userData?.dailyAffirmationDate === today
        ? userData?.dailyAffirmation || null
        : null;

      set({
        checkin,
        completedToday,
        dailyAffirmation,
        loading: false,
      });
    } catch (error) {
      set({
        error: getErrorMessage(error),
        loading: false,
      });
    }
  },

  doCheckin: async (data) => {
    set({ loading: true, error: null });

    try {
      const result = await submitCheckin(data);

      set({
        checkin: result.checkin,
        creditsSummary: result.creditsSummary,
        dailyAffirmation: result.dailyAffirmation,
        loading: false,
      });
    } catch (error) {
      set({
        error: getErrorMessage(error),
        loading: false,
      });
      throw error;
    }
  },

  completeAction: async (actionId, helped = null) => {
    set({ loading: true, error: null });
    
    try {
      const result = await recordCompletion({ actionId, helped });
      
      set(state => ({
        completedToday: [...state.completedToday, actionId],
        creditsSummary: {
          momentum7d: result.momentum7d,
          totalCredits: result.totalCredits,
        },
        lastReward: result,
        loading: false,
      }));
      
      return result;
    } catch (error) {
      set({
        error: getErrorMessage(error),
        loading: false,
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
  clearReward: () => set({ lastReward: null }),
}));

/**
 * Performance-optimized selectors using shallow comparison
 * Use these instead of selecting entire state slices to prevent re-renders
 *
 * Pattern: Use individual selectors for frequently-changing values
 * to prevent cascading re-renders
 */

// Select only checkin data (most commonly accessed)
export const useTodayCheckin = () => useTodayStore(state => state.checkin);

// Select completed actions (array - use for checking completions)
export const useTodayCompletedActions = () => useTodayStore(state => state.completedToday);

// Select credits summary (use for display only)
export const useTodayCreditsSummary = () => useTodayStore(state => state.creditsSummary);

// Select loading state (use for conditional rendering)
export const useTodayLoading = () => useTodayStore(state => state.loading);

// Select error state
export const useTodayError = () => useTodayStore(state => state.error);

// Select daily affirmation
export const useDailyAffirmation = () => useTodayStore(state => state.dailyAffirmation);

// Select last reward (for celebration modals)
export const useLastReward = () => useTodayStore(state => state.lastReward);

// Actions-only selector (stable reference, no re-renders on state change)
export const useTodayActions = () => useTodayStore(state => ({
  loadToday: state.loadToday,
  doCheckin: state.doCheckin,
  completeAction: state.completeAction,
  clearError: state.clearError,
  clearReward: state.clearReward,
}));

// Check if user has completed checkin today (derived state)
export const useHasCheckedIn = () => useTodayStore(state => state.checkin !== null);

// Get current streak (derived from creditsSummary)
export const useCurrentStreak = () => useTodayStore(state => state.creditsSummary.momentum7d);
